<template>
  <div class="stories-slider">
    <div class="slider-track">
      <transition :name="firstSlideTransition">
        <Slide
          v-if="currentStoryIndex === -1"
          :custom="true"
          :even="false"
          :active="true"
          :customClass="'first-slide'"
          :soundRun="soundRun"
        >
          <template v-slot:left-side>
            <CategoriesList
              v-if="isMobileView"
              :class="{ hidden, 'hidden-active': hiddenActive }"
              :useActive="false"
            />
            <div class="content" v-if="!isMobileView" :class="{ hidden }">
              <div v-html="strings.firstSlideText"></div>
              <div class="buttons">
                <div class="btn" @click="openStoryWithSound">
                  {{ strings.firstSlideBtnTextWithSound }}
                </div>
                <div class="btn btn-blue" @click="openStory">
                  {{ strings.firstSlideBtnText }}
                </div>
              </div>
            </div>
          </template>
          <template v-slot:right-side>
            <CategoriesList
              v-if="!isMobileView"
              :class="{ hidden, 'hidden-active': hiddenActive }"
              :useActive="false"
            />
            <div class="content" v-if="isMobileView" :class="{ hidden }">
              <div v-html="strings.firstSlideText"></div>
              <div class="buttons">
                <div class="btn" @click="openStoryWithSound">
                  {{ strings.firstSlideBtnTextWithSound }}
                </div>
                <div class="btn btn-blue" @click="openStory">
                  {{ strings.firstSlideBtnText }}
                </div>
              </div>
            </div>
          </template>
        </Slide>
      </transition>
      <transition name="changeSlide">
        <Slide
          v-if="currentStory"
          :even="isMobileView || currentStoryIndex % 2 == 0"
          :active="true"
          :category="storyCategory(currentStory.categoryId)"
          :soundRun="soundRun"
        />
      </transition>
      <transition name="slideUp">
        <Slide
          v-if="currentStoryIndex === stories.length"
          :custom="true"
          :twoSides="false"
          :active="true"
          :customClass="'last-slide'"
          :soundRun="soundRun"
        >
          <MainPage :style="{ paddingTop: 0 }">
            <template v-slot:ghost-text>{{
              strings.lastSlideGhostText
            }}</template>
            <template v-slot:text-content>
              <div v-html="strings.lastSlideText"></div>
              <a href="#" class="btn start" @click.prevent="changeStory(0)">{{
                strings.lastSlideBtnText
              }}</a>
            </template>
          </MainPage>
        </Slide>
      </transition>
    </div>
    <SliderNavigation :theme="navigationTheme" />
    <Lightbox
      v-if="currentStory"
      :visible="lightboxVisible"
      :imgs="[{ src: currentStory.image, title: currentStory.title }]"
      @hide="toggleLightbox"
    />
    <div class="progress-bar">
      <div
        class="active-progress"
        :class="{ clearing: autoplayCurrentProgress === 0 }"
        :style="{
          width: `${autoplayCurrentProgress}%`,
          transitionDuration: `${this.currentStory.duration / 1000}s`,
        }"
      ></div>
    </div>
  </div>
</template>

<script>
import Slide from "./Slide.vue";
import CategoriesList from "./CategoriesList.vue";
import MainPage from "./MainPage.vue";
import SliderNavigation from "./SliderNavigation.vue";
import Lightbox from "vue-easy-lightbox";
import { mapActions, mapGetters, mapState } from "vuex";

export default {
  data() {
    return {
      isTransition: true,
      hidden: true,
      hiddenActive: true,
      firstSlideTransition: "fadeIn",
      soundRun: false,
    };
  },
  computed: {
    ...mapState([
      "soundPlaying",
      "categories",
      "stories",
      "currentStoryIndex",
      "navigationTheme",
      "lightboxVisible",
      "autoplayDelay",
      "autoplayCurrentProgress",
      "headerHeight",
      "strings",
    ]),
    ...mapGetters(["currentStory", "isMobileView"]),
  },
  mounted() {
    this.handleTransitions();
  },
  methods: {
    ...mapActions([
      "changePage",
      "changeStory",
      "openCategory",
      "toggleLightbox",
      "toggleSound"
    ]),
    handleTransitions() {
      this.hidden = true;
      this.hiddenActive = true;
      setTimeout(() => {
        this.hidden = false;
        this.firstSlideTransition = "changeSlide";
        setTimeout(() => {
          this.hiddenActive = false;
        }, 1000);
      }, 500);
    },
    addInterval() {
      this.currentProgress += 1;
    },
    clearAutoplayInterval() {
      this.currentProgress = 0;
    },
    startTransition() {
      this.isTransition = true;
    },
    endTransition() {
      this.isTransition = false;
    },
    storyCategory(categoryId) {
      return this.categories.find((c) => c.id === categoryId);
    },
    openStoryWithSound() {
      this.changeStory(0);
      this.soundRun = true;
    },
    openStory() {
      this.changeStory(0);
      this.toggleSound(false);
    },
  },
  components: { Slide, CategoriesList, MainPage, SliderNavigation, Lightbox },
  watch: {
    currentStoryIndex(newIndex) {
      if (newIndex === -1) {
        this.handleTransitions();
      }
    },
  },
};
</script>

<style lang="scss">
.stories-slider {
  position: relative;
  will-change: auto;
  height: 100%;
  .slider-track {
    height: 100%;
    background: linear-gradient(222.35deg, #1e82f5 0%, #002277 98.59%);
  }
}
#portrait {
  .stories-slider {
    .slider-track {
      background: none;
    }
  }
}
.progress-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  z-index: 99;
  .active-progress {
    transition-timing-function: linear;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background-color: #fff;
    &.clearing {
      transition-duration: 0s !important;
    }
  }
}
.buttons {
  margin-left: -10px;
  .btn {
    margin-top: 10px;
    margin-left: 10px;
    margin-right: 10px;
  }
}

.first-slide {
  .content {
    p,
    .btn {
      transition-duration: 0.8s;
    }
    p {
      &:nth-child(1) {
        transition-delay: 0.3s;
      }
      &:nth-child(2) {
        transition-delay: 0.6s;
      }
    }
    .btn {
      transition-delay: 1s;
    }
    &.hidden {
      p,
      .btn {
        opacity: 0;
        transform: translateY(50%);
      }
    }
  }
}

.twoSides.changeSlide-enter {
  .left-side,
  .right-side {
    opacity: 0;
    .side-inner {
      &:after {
        opacity: 0;
      }
      .slide-image {
        .image-wrapper {
          opacity: 0;
          transform: rotate(0) !important;
        }
      }
    }
  }
  .category-container {
    opacity: 0;
    transform: translateX(100%);
  }
  &.even {
    .category-container {
      opacity: 0;
      transform: translateX(-100%);
    }
  }
  .left-side {
    transform: skewX(-12deg) translate3d(0, -50%, 0);
  }
  .right-side {
    transform: skewX(-12deg) translate3d(0, 50%, 0);
  }
  .slide-title,
  .description,
  .slide-text {
    opacity: 0;
    transform: translateY(-40px);
  }
  .sound {
    opacity: 0;
  }
  &.first-slide {
    .left-side {
      .side-inner {
        .content {
          p,
          .btn {
            opacity: 0;
          }
        }
      }
    }
  }
}

.twoSides.changeSlide-enter-active {
  transition: 1.6s;
  .left-side,
  .right-side {
    transition: 0.5s;
    .side-inner {
      &:after {
        transition: 0.5s 0.4s;
      }
      .slide-image {
        .image-wrapper {
          transition: 0.5s 0.4s;
        }
      }
    }
  }
  .slide-title {
    transition: 0.5s 0.3s;
  }
  .description {
    transition: 0.5s 0.4s;
  }
  .slide-text {
    transition: 0.5s 0.6s;
  }
  .category-container {
    transition: 0.5s 1s;
  }
  .sound {
    transition: 0.5s 1.2s;
  }
  &.first-slide {
    .left-side {
      .side-inner {
        .content {
          p {
            transition: 0.5s 0.5s;
          }
          .btn {
            transition: 0.5s 0.8s;
          }
        }
      }
    }
  }
}

.twoSides.changeSlide-leave-active {
  transition: 1s;
  .left-side,
  .right-side {
    opacity: 0;
    transition: 0.5s 0.5s;
    .side-inner {
      &:after {
        opacity: 0;
        transition: 0.3s 0.3s;
      }
      .slide-image {
        .image-wrapper {
          opacity: 0;
          transform: rotate(0) !important;
          transition: 0.3s 0.3s;
        }
      }
    }
  }
  .category-container {
    opacity: 0;
    transition: 0.4s 0.3s;
  }
  .left-side {
    transform: skewX(-12deg) translate3d(0, -50%, 0);
    .category-container {
      transform: translateX(-100%);
    }
  }
  .right-side {
    transform: skewX(-12deg) translate3d(0, 50%, 0);
    .category-container {
      transform: translateX(100%);
    }
  }
  .slide-title,
  .description,
  .slide-text {
    opacity: 0;
    transform: translateY(-40px);
    transition: 0.5s 0.3s;
  }
  .sound {
    opacity: 0;
    transition: 0.5s, 0.3s;
  }
  &.first-slide {
    .left-side {
      .side-inner {
        .content {
          p {
            opacity: 0;
            transition: 0.3s 0.2s;
          }
          .btn {
            opacity: 0;
            transition: 0.3s 0.2s;
          }
        }
      }
    }
  }
}
#portrait {
  .twoSides.changeSlide-leave-active {
    .category-container {
      transform: translateX(-100%);
    }
  }
}
@media only screen and (max-width: 768px) {
  .stories-slider {
    .slider-navigation {
      .slider-arrows {
        height: 26px;
        .arrow {
          width: 15px;
        }
      }
    }
  }
}
</style>